// URL-encoded filter state for the public-hearings page.
//
// Round-trips between the browser's URL search-params and a typed
// `FilterState` object. All filtering runs client-side over the small
// upstream payload (caps at 200 items, typically ~30); the page does
// not pass filters to the upstream API.
//
// URL params:
//   q                free-text substring across title, description,
//                    community, address, bylaw numbers, and LOC codes
//                    (`applyFilters.matchesQuery` is the source of
//                    truth for the searched fields)
//   community        a single community name selected from the
//                    "Find near…" autocomplete (single-select)
//   ward             repeated; ward number(s) to include
//                    (e.g. `?ward=1&ward=7`); intersects against
//                    `Hearing.wards`
//   status           repeated; one or more of `open` / `closing` /
//                    `closed`. `closing` = submission deadline within
//                    48h; `closed` = deadline passed; `open` = neither
//   type             repeated; one or more of `land_use` /
//                    `outline_plan` / `policy`. Derived per-row from
//                    title heuristics + presence of district codes;
//                    a single hearing can match multiple types
//   hearing_after    ISO date (YYYY-MM-DD); inclusive
//   hearing_before   ISO date (YYYY-MM-DD); inclusive
//
// The page is grouped by hearing date, so the date window filters
// against the hearing's meeting date — not the submission deadline.

export type HearingStatus = "open" | "closing" | "closed";
export type HearingType = "land_use" | "outline_plan" | "policy";

export const HEARING_STATUSES: readonly HearingStatus[] = [
  "open",
  "closing",
  "closed",
] as const;
export const HEARING_TYPES: readonly HearingType[] = [
  "land_use",
  "outline_plan",
  "policy",
] as const;

const HEARING_STATUS_SET = new Set<string>(HEARING_STATUSES);
const HEARING_TYPE_SET = new Set<string>(HEARING_TYPES);

export type FilterState = {
  q: string;
  community: string | null;
  wards: number[];
  statuses: HearingStatus[];
  types: HearingType[];
  hearingAfter: string | null;
  hearingBefore: string | null;
};

export const EMPTY_FILTER_STATE: FilterState = {
  q: "",
  community: null,
  wards: [],
  statuses: [],
  types: [],
  hearingAfter: null,
  hearingBefore: null,
};

export function encodeFilterState(state: FilterState): URLSearchParams {
  const sp = new URLSearchParams();
  if (state.q) sp.set("q", state.q);
  if (state.community) sp.set("community", state.community);
  for (const w of state.wards) sp.append("ward", String(w));
  for (const s of state.statuses) sp.append("status", s);
  for (const t of state.types) sp.append("type", t);
  if (state.hearingAfter) sp.set("hearing_after", state.hearingAfter);
  if (state.hearingBefore) sp.set("hearing_before", state.hearingBefore);
  return sp;
}

export function parseFilterState(
  input: URLSearchParams | Record<string, string | string[] | undefined>,
): FilterState {
  const sp =
    input instanceof URLSearchParams ? input : recordToSearchParams(input);

  // Wards: parse repeated `ward` params, drop anything that isn't a
  // positive integer, dedupe, and keep numeric order so the URL is
  // stable across reloads.
  const wards = Array.from(
    new Set(
      sp.getAll("ward").flatMap((v) => {
        const n = Number(v);
        return Number.isInteger(n) && n > 0 ? [n] : [];
      }),
    ),
  ).sort((a, b) => a - b);

  // Statuses / types: dedupe, drop unknown values, preserve the
  // canonical ordering (HEARING_STATUSES / HEARING_TYPES) for URL
  // stability.
  const statusSet = new Set(
    sp.getAll("status").filter((v): v is HearingStatus =>
      HEARING_STATUS_SET.has(v),
    ),
  );
  const statuses = HEARING_STATUSES.filter((s) => statusSet.has(s));

  const typeSet = new Set(
    sp.getAll("type").filter((v): v is HearingType => HEARING_TYPE_SET.has(v)),
  );
  const types = HEARING_TYPES.filter((t) => typeSet.has(t));

  return {
    q: sp.get("q") ?? "",
    community: sp.get("community") || null,
    wards,
    statuses,
    types,
    hearingAfter: sp.get("hearing_after") || null,
    hearingBefore: sp.get("hearing_before") || null,
  };
}

function recordToSearchParams(
  rec: Record<string, string | string[] | undefined>,
): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(rec)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, v);
    } else {
      sp.append(key, value);
    }
  }
  return sp;
}
